"use client";

import { useEffect } from "react";

/**
 * Handles Next.js chunk loading errors by:
 * 1. Detecting chunk loading failures
 * 2. Clearing stale cache
 * 3. Reloading the page to fetch fresh chunks
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      const error = event.error;
      const errorMessage = event.message || "";
      
      // Check error object properties
      const errorName = error?.name || "";
      const errorMsg = error?.message || "";
      const errorString = String(error);

      // Check if this is a chunk loading error
      const isChunkError =
        errorName === "ChunkLoadError" ||
        errorString.includes("Loading chunk") ||
        errorString.includes("ChunkLoadError") ||
        errorString.includes("_next/static/chunks") ||
        errorMessage.includes("Loading chunk") ||
        errorMessage.includes("ChunkLoadError") ||
        errorMsg.includes("Loading chunk") ||
        errorMsg.includes("ChunkLoadError") ||
        (errorString.includes("Failed to fetch") &&
          errorString.includes("chunk"));

      if (isChunkError) {
        // Clear all caches
        if ("caches" in window) {
          caches.keys().then((cacheNames) => {
            return Promise.all(
              cacheNames.map((cacheName) => caches.delete(cacheName))
            );
          });
        }

        // Unregister service workers
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => {
              registration.unregister();
            });
          });
        }

        // Reload the page to fetch fresh chunks
        // Use a small delay to ensure cache clearing completes
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };

    // Listen for unhandled errors
    window.addEventListener("error", handleChunkError);

    // Also listen for unhandled promise rejections (chunk errors often come as promise rejections)
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorName = error?.name || "";
      const errorMsg = error?.message || "";
      const errorString = String(error);

      const isChunkError =
        errorName === "ChunkLoadError" ||
        errorString.includes("Loading chunk") ||
        errorString.includes("ChunkLoadError") ||
        errorString.includes("_next/static/chunks") ||
        errorMsg.includes("Loading chunk") ||
        errorMsg.includes("ChunkLoadError") ||
        (errorString.includes("Failed to fetch") &&
          errorString.includes("chunk"));

      if (isChunkError) {
        // Clear caches and reload (same logic as above)
        if ("caches" in window) {
          caches.keys().then((cacheNames) => {
            return Promise.all(
              cacheNames.map((cacheName) => caches.delete(cacheName))
            );
          });
        }

        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.getRegistrations().then((registrations) => {
            registrations.forEach((registration) => {
              registration.unregister();
            });
          });
        }

        setTimeout(() => {
          window.location.reload();
        }, 100);
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleChunkError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return null;
}

